import React, { useState, useRef, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, Loader2, AlertTriangle, FileText, MessageSquare, ListChecks, CalendarRange, BedDouble, Mail, HeartHandshake } from "lucide-react";

const REF_ICON = { resident: BedDouble, care_task: HeartHandshake, task: ListChecks, event: CalendarRange, chat: MessageSquare, email: Mail };

export default function Assistant() {
  const { t, lang } = useT();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef();

  const examples = lang === "fr"
    ? ["Quelles tâches restent à compléter aujourd'hui?", "Résume les rapports d'aujourd'hui.", "Quand est le prochain rendez-vous de Gérard?", "Y a-t-il eu un incident cette semaine?"]
    : ["What tasks still need to be completed today?", "Summarize today's reports.", "When is Gérard's next appointment?", "Has anyone reported an incident this week?"];

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/ask", { question });
      setMessages((m) => [...m, { role: "ai", text: data.answer, references: data.references, urgent: data.urgent }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: formatApiError(e.response?.data?.detail) || "Error", references: [] }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="h-full flex flex-col max-w-3xl mx-auto w-full">
      <div className="p-4 sm:p-6 lg:px-8 lg:pt-8 lg:pb-4">
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></span>
          {t("assistant")}
        </h1>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 sm:px-6 lg:px-8 space-y-4">
        {messages.length === 0 && (
          <div className="pt-8">
            <p className="text-muted-foreground mb-3 text-sm font-medium">{t("tryAsking")}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {examples.map((ex) => (
                <button key={ex} onClick={() => ask(ex)} data-testid="example-question"
                  className="text-left text-sm p-3 rounded-xl border border-border hover:border-primary hover:bg-accent/50 transition-colors">{ex}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] ${m.role === "user" ? "bg-primary text-white rounded-2xl rounded-br-sm" : "bg-card border border-border rounded-2xl rounded-bl-sm"} px-4 py-3`} data-testid={m.role === "ai" ? "ai-answer" : "user-question"}>
              {m.urgent && <Badge className="bg-destructive text-white mb-2"><AlertTriangle className="w-3 h-3 mr-1" />{t("urgentFlag")}</Badge>}
              <p className="text-sm whitespace-pre-wrap">{m.text}</p>
              {m.references?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border/60">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{t("references")}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {m.references.map((r, j) => {
                      const Icon = REF_ICON[r.type] || FileText;
                      return <span key={j} title={r.snippet} className="text-[11px] px-2 py-1 rounded-md bg-muted flex items-center gap-1 max-w-[220px] truncate"><Icon className="w-3 h-3 shrink-0" />{r.label}</span>;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> …</div></div>}
      </div>

      <div className="p-4 sm:p-6 lg:px-8 lg:pb-8">
        <div className="flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder={t("askPlaceholder")} className="h-12" data-testid="assistant-input" />
          <Button size="icon" className="h-12 w-12" onClick={() => ask()} disabled={loading} data-testid="assistant-send"><Send className="w-5 h-5" /></Button>
        </div>
      </div>
    </div>
  );
}
